import { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { GameOver } from '../games/GameShell.js';
import { QuokkaChomp } from '../games/QuokkaChomp.js';
import { QuokkaRacer } from '../games/QuokkaRacer.js';
import { StarPopper } from '../games/StarPopper.js';

type Content =
  | { game: 'quokka-chomp'; step: number; start: number; ceiling: number }
  | { game: 'quokka-racer'; gates: { question: string; answer: number; options: number[] }[] }
  | { game: 'star-popper'; makes: number; pairs: [number, number][] };

interface Offer {
  key: string;
  name: string;
  invitation: string;
  seconds: number;
}

interface Started {
  gameSessionId: string;
  key: string;
  name: string;
  seconds: number;
  content: Content;
}

/**
 * The breather.
 *
 * Offered, never started for them: a screen that launches a game on its own is
 * a screen deciding how long a child plays. One a day, and the way out is
 * always on screen.
 *
 * Everything here is built from material the child has already mastered, and
 * none of it is assessed - the server will not let a game touch a score.
 */
export function Games() {
  const navigate = useNavigate();
  const [playing, setPlaying] = useState<Started | null>(null);
  const [finished, setFinished] = useState<{ name: string; line: string } | null>(null);

  const offer = useQuery({
    queryKey: ['games', 'offer'],
    queryFn: () => api<{ available: Offer[] }>('/child/games/offer'),
  });

  const start = useMutation({
    mutationFn: (key: string) =>
      api<Started>('/child/games/start', {
        method: 'POST',
        body: { gameKey: key, reason: 'finisher' },
      }),
    onSuccess: (data) => setPlaying(data),
  });

  const end = useMutation({
    mutationFn: ({ id, score }: { id: string; score: number }) =>
      api(`/child/games/${id}/end`, { method: 'POST', body: { score } }),
  });

  // Stable identity. It was a plain function, so every render handed the
  // games a new onFinish and restarted their clocks.
  const done = useCallback(
    (score: number) => {
      if (!playing) return;
      end.mutate({ id: playing.gameSessionId, score });
      setFinished({
        name: playing.name,
        line: linesFor(playing.key, score),
      });
      setPlaying(null);
    },
    [playing, end],
  );

  if (finished) {
    return (
      <main className="child">
        <GameOver name={finished.name} line={finished.line} onDone={() => navigate('/play')} />
      </main>
    );
  }

  if (playing) {
    const { content } = playing;
    return (
      <main className="child">
        {content.game === 'quokka-chomp' && (
          <QuokkaChomp
            step={content.step}
            start={content.start}
            ceiling={content.ceiling}
            seconds={playing.seconds}
            onFinish={done}
          />
        )}
        {content.game === 'quokka-racer' && (
          <QuokkaRacer gates={content.gates} seconds={playing.seconds} onFinish={done} />
        )}
        {content.game === 'star-popper' && (
          <StarPopper
            makes={content.makes}
            pairs={content.pairs}
            seconds={playing.seconds}
            onFinish={done}
          />
        )}
      </main>
    );
  }

  return (
    <main className="child">
      <h1>Stretch your legs</h1>

      {offer.isLoading && <p>Looking&hellip;</p>}

      {offer.data?.available.length === 0 && (
        <>
          <p>You have had your game today. There is always tomorrow!</p>
          <button type="button" className="btn btn--big" onClick={() => navigate('/play')}>
            Back to the adventure
          </button>
        </>
      )}

      <div className="game-choices">
        {offer.data?.available.map((game) => (
          <button
            key={game.key}
            type="button"
            className="game-choice"
            disabled={start.isPending}
            onClick={() => start.mutate(game.key)}
          >
            <strong>{game.name}</strong>
            <span>{game.invitation}</span>
          </button>
        ))}
      </div>

      <button type="button" className="btn btn--quiet" onClick={() => navigate('/play')}>
        No thanks, keep going
      </button>
    </main>
  );
}

/** Warm, and never a score to beat. */
function linesFor(key: string, score: number): string {
  if (score === 0) return 'That was a tricky one. Next time!';
  if (key === 'quokka-chomp') return `You chomped ${score} in the right order.`;
  if (key === 'quokka-racer') return `You drove through ${score} gates.`;
  return `You popped ${score} pairs.`;
}
